'use client'

/**
 * Purpose: Pin a sticky peer's `top` to the fixed navbar's live bottom edge as
 * it hides/shows on scroll, and report whether the peer has reached that
 * pinned position yet — used by ProductsStickyHeader on both breakpoints.
 * Inputs: a ref to the sticky element and the navbar's current hidden flag
 * (from useNavbarScrollHidden).
 * Outputs: none (imperatively writes `ref.current.style.top` every frame);
 * returns `isStuck`, a latched boolean consumers read for `data-stuck`.
 */

import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { findNavbar, useNavbarHeight } from '@/hooks/use-navbar-height'
import { NAVBAR_HIDE_DURATION_S, NAVBAR_SCROLL_HIDE_OFFSET } from '@/hooks/use-navbar-scroll-hidden'

/** A little past the navbar transition, so the frame loop always reaches the target. */
export const NAVBAR_STICKY_TOP_SETTLE_MS = NAVBAR_HIDE_DURATION_S * 1000 + 120

/** Within this many px, the peer's live position counts as pinned to its tracked `top`. */
const STUCK_EPSILON_PX = 1

/**
 * Keep a sticky peer's `top` flush with the fixed navbar as it hides/shows on
 * scroll — e.g. the products page "recommended" header — and report whether
 * the peer has actually reached that pinned position yet.
 *
 * `navbarHidden` still comes from the peer's own `useNavbarScrollHidden()` so
 * `data-navbar-hidden` and the freeze window stay in the same render. Hide
 * state itself is a module-scoped store (every consumer sees the same flag).
 *
 * The written `top` is the navbar's current *visible bottom edge* — always the
 * live `getBoundingClientRect().bottom`, not a boolean-derived snap to
 * `navbarHeight` or `0`. That way a stale `navbarHidden` (a second
 * `useNavbarScrollHidden` subscription that missed the hide) cannot leave the
 * peer pinned at the full bar height with the bar already off-screen — the
 * chips-and-Filters strip floating mid-viewport over the product grid.
 * `navbarHidden` is only the fallback when no navbar element is mounted.
 *
 * The per-frame `top` is written **imperatively** to `ref.current.style.top`
 * (never through React state): it lands before the compositor paints each frame,
 * with no subtree reconciliation, so the opaque header stays pixel-flush with the
 * bar even under a fast scroll that starves React's commit phase. Owning `top`
 * outside React also means an unrelated re-render mid-animation (e.g. the
 * scroll-spy changing the active chip) can't reapply a stale `top` and yank the
 * header. Before the first frame runs the CSS `top: 0` fallback holds a
 * top-of-viewport pin (SSR / first paint) rather than a mid-page gap.
 *
 * This same frame-tracked `top` also drives the mobile products header, which
 * additionally collapses its own height via CSS (`data-navbar-hidden`, gated on
 * `data-stuck` — see productsStickyHeader.css / ProductsStickyHeader.tsx). A
 * fixed, un-tracked `top` there previously left a gap between the animating
 * navbar and the header sitting at its final resting position — frame-tracking
 * keeps the header's top edge glued to the navbar's real bottom edge throughout
 * the hide/show, so no background ever shows between them.
 *
 * `isStuck` — whether the peer's live position has actually caught up to that
 * `top` — is derived from the *same* per-frame measurement that writes `top`,
 * not polled back out by a second, independently-scheduled effect. Two hooks
 * racing their own rAF loops against the same `el.style.top` can land a frame
 * apart under fast scrolling, which previously left a consumer's "is it
 * stuck yet" read stuck at `false` even once the peer was visibly pinned.
 * Once stuck, `isStuck` latches `true` until the visitor scrolls back within
 * `NAVBAR_SCROLL_HIDE_OFFSET` of the top — a scroll-up that only reveals the
 * navbar must not report "unstuck" (and, for ProductsStickyHeader, must not
 * re-expand the collapsed title).
 */
export function useNavbarStickyTop(
  ref: RefObject<HTMLElement | null>,
  navbarHidden: boolean,
): boolean {
  const navbarHeight = useNavbarHeight()
  const rafRef = useRef<number | null>(null)
  const [isStuck, setIsStuck] = useState(false)
  const latchedStuckRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const tick = (): boolean => {
      const el = ref.current
      if (!el) return false

      // Never snap to a boolean-derived target — live bottom is the source of
      // truth, including when navbarHidden is stale.
      const navbar = findNavbar()
      let top: number
      if (navbar) {
        top = Math.max(0, navbar.getBoundingClientRect().bottom)
      } else if (navbarHeight != null) {
        top = navbarHidden ? 0 : navbarHeight
      } else {
        return false
      }
      el.style.top = `${top}px`

      const nowStuck = el.getBoundingClientRect().top <= top + STUCK_EPSILON_PX
      if (nowStuck) {
        latchedStuckRef.current = true
      } else if (window.scrollY <= NAVBAR_SCROLL_HIDE_OFFSET) {
        latchedStuckRef.current = false
      }
      setIsStuck(latchedStuckRef.current || nowStuck)

      return navbar != null
    }

    const hasNavbar = tick()
    // No rAF (SSR / non-browser) or no measurable bar (jsdom): the snap above is
    // the resting position — there is nothing to animate.
    if (typeof requestAnimationFrame === 'undefined' || !hasNavbar) {
      return
    }

    const now = () =>
      typeof performance !== 'undefined' ? performance.now() : Date.now()
    // Each scroll extends this window so a hide that the boolean missed still
    // gets tracked for the whole 0.35s transform, not just one frame.
    let startedAt = now()

    // Writes go straight to the DOM inside the frame loop (never React state,
    // besides the derived `isStuck`) so we track the bar's real position
    // without cascading renders.
    const loop = () => {
      tick()
      if (now() - startedAt >= NAVBAR_STICKY_TOP_SETTLE_MS) {
        rafRef.current = null
        return
      }
      rafRef.current = requestAnimationFrame(loop)
    }

    const onScroll = () => {
      startedAt = now()
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [navbarHidden, navbarHeight, ref])

  return isStuck
}
